import {
  type DeleteInboxConversationMutation,
  type FetchResult,
  type MutationResult,
  useDeleteInboxConversationMutation,
} from "@qw/graphql";
import { useOpenConfirmation, useToast } from "@qw/ui";
import { useCallback } from "react";

import { useInboxQueryStates } from "./inbox-query-states-provider";

export type UseDeleteConversationReturn = [
  (id: string) => Promise<FetchResult<DeleteInboxConversationMutation> | undefined>,
  MutationResult<DeleteInboxConversationMutation>,
];

export const useDeleteConversation = (): UseDeleteConversationReturn => {
  const { toast } = useToast();
  const { openConfirmation } = useOpenConfirmation();
  const { conversationId: currentConversationId, setConversationId: setCurrentConversationId } = useInboxQueryStates();

  const [deleteConversationMutation, result] = useDeleteInboxConversationMutation({
    refetchQueries: ["InboxConversations", "InboxLabelUnreadCounts"],
  });

  const deleteConversation = useCallback(
    async (id: string) => {
      const confirmed = await openConfirmation({
        title: "Delete conversation",
        description: "Are you sure you want to delete this conversation? This action cannot be undone.",
        buttonProps: { theme: "destructive", children: "Delete" },
      });

      if (!confirmed) {
        return;
      }

      return deleteConversationMutation({
        variables: { id },
        onCompleted: (data) => {
          if (!data?.deleteInboxConversation) {
            throw new Error("Failed to delete conversation");
          }
          if (currentConversationId === id) {
            setCurrentConversationId(null, { history: "replace" });
          }
        },
        onError: (error) => {
          toast({
            title: "Error deleting conversation",
            description: error instanceof Error ? error.message : "An unexpected error occurred",
            variant: "destructive",
          });
        },
      });
    },
    [deleteConversationMutation, openConfirmation, toast, currentConversationId, setCurrentConversationId]
  );

  return [deleteConversation, result];
};
